const DEFAULT_API_URL = 'https://revision.injaz.fr';
const OUTPUT_SAMPLE_RATE = 16000;

export const RECITATION_API_URL = (process.env.REACT_APP_TRANSCRIPTION_API_URL || DEFAULT_API_URL).replace(/\/+$/, '');

function toWebSocketUrl() {
  const url = new URL(`${RECITATION_API_URL}/ws/recite`);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

function downsampleToPcm16(input, inputSampleRate) {
  if (inputSampleRate === OUTPUT_SAMPLE_RATE) {
    return floatToPcm16(input);
  }

  if (inputSampleRate < OUTPUT_SAMPLE_RATE) {
    throw new Error(`Le microphone utilise ${inputSampleRate} Hz, fréquence insuffisante pour le serveur.`);
  }

  const ratio = inputSampleRate / OUTPUT_SAMPLE_RATE;
  const outputLength = Math.round(input.length / ratio);
  const output = new Float32Array(outputLength);

  for (let index = 0; index < outputLength; index += 1) {
    const start = Math.floor(index * ratio);
    const end = Math.min(Math.ceil((index + 1) * ratio), input.length);
    let sum = 0;
    let count = 0;

    for (let sourceIndex = start; sourceIndex < end; sourceIndex += 1) {
      sum += input[sourceIndex];
      count += 1;
    }

    output[index] = count ? sum / count : 0;
  }

  return floatToPcm16(output);
}

function floatToPcm16(input) {
  const output = new Int16Array(input.length);
  for (let index = 0; index < input.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, input[index]));
    output[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return output;
}

export class RecitationSession {
  constructor({
    surah,
    ayah,
    continuous = false,
    maxAyahs = null,
    startAfter = false,
    onMessage,
    onError,
    onClose,
  }) {
    this.surah = surah;
    this.ayah = ayah;
    this.continuous = continuous;
    this.maxAyahs = maxAyahs;
    this.startAfter = startAfter;
    this.onMessage = onMessage;
    this.onError = onError;
    this.onClose = onClose;
    this.socket = null;
    this.mediaStream = null;
    this.audioContext = null;
    this.source = null;
    this.processor = null;
    this.muteGain = null;
    this.recording = false;
    this.stopping = false;
    this.destroyed = false;
    this.cleaned = false;
  }

  async start() {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('La capture du microphone n’est pas disponible dans ce navigateur.');
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) {
      throw new Error('La capture audio n’est pas supportée par ce navigateur.');
    }

    try {
      // Traitements navigateur DÉSACTIVÉS pour la reconnaissance.
      //
      // `noiseSuppression` est une soustraction spectrale conçue pour la voix
      // au téléphone : elle prend les fricatives (س ش ص ث ح خ) pour du bruit et
      // les rabote — or ce sont précisément les phonèmes qui portent les
      // distinctions de tajwid. `autoGainControl` fait varier le niveau en
      // cours de phrase et déforme l'enveloppe spectrale. Le modèle ayant été
      // entraîné sur de la récitation non traitée, ces filtres creusent un
      // écart entre les conditions d'entraînement et l'usage réel.
      this.mediaStream = await navigator.mediaDevices
        .getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          },
        })
        .catch(() => (
          // Certains périphériques refusent ces contraintes : mieux vaut
          // capturer avec les réglages par défaut que ne rien capturer.
          navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1 } })
        ));

      // Demander 16 kHz directement laisse le navigateur rééchantillonner en
      // code natif, avec un vrai filtre anti-repliement. `downsampleToPcm16`
      // reste le repli si la fréquence matérielle est imposée.
      try {
        this.audioContext = new AudioContextClass({ sampleRate: OUTPUT_SAMPLE_RATE });
      } catch (unsupportedRate) {
        this.audioContext = new AudioContextClass();
      }
      if (!this.audioContext.audioWorklet) {
        throw new Error('La capture audio moderne n’est pas supportée par ce navigateur.');
      }
      await this.audioContext.audioWorklet.addModule(
        new URL('/audio-worklet.js', window.location.origin).toString()
      );
      await this.audioContext.resume();
      this.source = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.processor = new AudioWorkletNode(this.audioContext, 'quran-audio-capture', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
      this.muteGain = this.audioContext.createGain();
      this.muteGain.gain.value = 0;

      this.processor.port.onmessage = (event) => {
        if (!this.recording || this.stopping || this.socket?.readyState !== WebSocket.OPEN) {
          return;
        }
        const pcm = downsampleToPcm16(event.data, this.audioContext.sampleRate);
        if (pcm.length > 0) {
          this.socket.send(pcm.buffer);
        }
      };

      this.source.connect(this.processor);
      this.processor.connect(this.muteGain);
      this.muteGain.connect(this.audioContext.destination);

      await this.connectSocket();
    } catch (error) {
      this.destroy();
      throw error;
    }
  }

  connectSocket() {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(toWebSocketUrl());
      socket.binaryType = 'arraybuffer';
      this.socket = socket;

      socket.onopen = () => {
        if (this.destroyed) {
          socket.close();
          return;
        }
        socket.send(JSON.stringify({
          type: 'start',
          surah: Number(this.surah),
          ayah: Number(this.ayah),
          auto_detect: false,
          continuous: Boolean(this.continuous),
          max_ayahs: this.maxAyahs == null ? null : Number(this.maxAyahs),
          start_after: Boolean(this.startAfter),
        }));
        resolve();
      };

      socket.onmessage = (event) => {
        let payload;
        try {
          payload = JSON.parse(event.data);
        } catch (error) {
          this.onError?.(new Error('Réponse WebSocket invalide reçue du serveur.'));
          return;
        }

        this.onMessage?.(payload);
        if (payload.type === 'started') {
          this.recording = true;
        }
        if (payload.type === 'stopped') {
          this.recording = false;
          this.cleanup();
          this.onClose?.();
        }
      };

      socket.onerror = () => {
        const error = new Error('Impossible de communiquer avec le service de transcription.');
        this.onError?.(error);
        reject(error);
      };

      socket.onclose = () => {
        this.recording = false;
        this.cleanup();
        this.onClose?.();
      };
    });
  }

  stop(reason = 'manual') {
    if (this.socket?.readyState === WebSocket.OPEN && !this.stopping) {
      this.stopping = true;
      this.recording = false;
      this.socket.send(JSON.stringify({ type: 'stop', reason }));
      return;
    }
    this.destroy();
  }

  destroy() {
    this.destroyed = true;
    this.recording = false;
    if (this.socket && this.socket.readyState < WebSocket.CLOSING) {
      this.socket.close();
    }
    this.cleanup();
  }

  cleanup() {
    if (this.cleaned) {
      return;
    }
    this.cleaned = true;

    if (this.processor) {
      this.processor.port.onmessage = null;
      this.processor.disconnect();
    }
    this.source?.disconnect();
    this.muteGain?.disconnect();
    this.mediaStream?.getTracks().forEach((track) => track.stop());
    this.audioContext?.close().catch(() => undefined);
    this.processor = null;
    this.source = null;
    this.muteGain = null;
    this.mediaStream = null;
    this.audioContext = null;
  }
}
